import Papa, { ParseResult } from 'papaparse';
import chardet from 'chardet';
import iconv from 'iconv-lite';
import {
  MissingRequiredCsvColumnsError,
  FileCannotReadbleError,
} from '@/utils/error';

// Canonical key → Acceptable CSV header names (JP + EN)
export const HEADER_ALIASES: Record<string, string[]> = {
  sdsID: [
    'ファイルの固有ID',
    'SDS ID',
    'File ID', // optional alias
    'sds_id',
  ],
  pdfName: [
    'SDSファイル名',
    'PDFのファイル名',
    'PDF File Name',
    'File Name',
    'file_name',
  ],
  riskAssessDate: [
    'SDSリスクアセスメント実施日',
    'リスクアセスメント実施日',
    'Risk Assessment Date',
    'risk_assessed_at',
  ],
  supplierConfirmationDate: [
    'サプライヤーへの確認日',
    'Supplier Confirmation Date',
    'supplied_verified_at',
  ],
  department: ['担当部署', 'Department', 'departments'],
  tag: ['タグ', 'Tags', 'tags'],
  productCode: ['製品コード', 'Product Code', 'product_code'],
  assignedUser: [
    '担当ユーザーメールアドレス',
    'Assigned User Email',
    'Assigned User Emails',
    'assigned_user_emails',
  ],
  // Optional extra column if needed
  notes: ['備考', 'notes', 'Notes'],
};

// Define the csvItem type (ensure this matches your actual type)
export interface csvItem {
  sdsID?: string;
  pdfName?: string;
  riskAssessDate?: string;
  supplierConfirmationDate?: string;
  department?: string;
  tag?: string;
  productCode?: string;
  assignedUser?: string;
  assignedUserIds?: string;
  notes?: string;
}

// Helper function to compare two sets
export const areSetsEqual = (set1: Set<string>, set2: Set<unknown>) => {
  if (set1.size !== set2.size) return false;
  for (const item of set1) {
    if (!set2.has(item)) return false;
  }
  return true;
};

export const filterDuplicateTextInArray = (
  stringArray: string[]
): { seen: string[]; duplicates: string[] } => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  stringArray.forEach((item) => {
    if (seen.has(item)) {
      duplicates.add(item);
    } else {
      seen.add(item);
    }
  });

  return {
    seen: Array.from(seen),
    duplicates: Array.from(duplicates),
  };
};

export const fixStr = (str: string): string => {
  return (
    str
      // .replace(/[\u003F\uFFFD]{2,}/g, '\u3231') // ?? or �� → ㈱ (multiple ?/�)
      // .replace(/[\u003F\uFFFD]/g, '\u3231') // Default: ? or � → ㈱
      .replace(/[\u2212\uFF0D]/g, '\u002D') // − or － → -
      .replace(/[\uFF1B\u003B]/g, '\uFF1B') // ; (U+003B) → ； (U+FF1B, full-width)
      .replace(/[\u301C\uFF5E]/g, '\uFF5E')
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .replace(/ _/g, '_') // Remove space before underscore
      .normalize('NFC') // Unicode normalization
      .trim()
  );
};

export const parseCSV = (file: File): Promise<csvItem[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        // Read the file as an ArrayBuffer
        const csvResult = e?.target?.result as ArrayBuffer;
        if (!csvResult) {
          throw new FileCannotReadbleError('Failed to read file content');
        }

        // Detect encoding
        const uint8Array = new Uint8Array(csvResult);
        let detectedEncoding = chardet.detect(uint8Array) || 'UTF-8';
        if (detectedEncoding === 'ISO-8859-1') {
          detectedEncoding = 'UTF-8';
        }

        // Decode the data
        let decodedData = iconv.decode(
          Buffer.from(csvResult),
          detectedEncoding
        );
        if (decodedData.charCodeAt(0) === 0xfeff) {
          decodedData = decodedData.slice(1); // Remove BOM
        }

        // Parse the CSV
        Papa.parse(decodedData, {
          header: true,
          skipEmptyLines: true,
          complete: (results: ParseResult<Record<string, string>>) => {
            try {
              // 1. Actual header names from CSV
              const actualHeaders = results.meta.fields || [];

              // 2. Map canonical key → actual header used
              const canonicalKeys = Object.keys(HEADER_ALIASES);
              const canonicalToActualHeader: Record<string, string> = {};

              for (const canonicalKey of canonicalKeys) {
                const aliases = HEADER_ALIASES[canonicalKey];
                const matched = aliases.find((alias) =>
                  actualHeaders.includes(alias)
                );
                if (matched) {
                  canonicalToActualHeader[canonicalKey] = matched;
                }
              }

              // 3. Validate required columns (excluding optional notes)
              const requiredKeys = canonicalKeys.filter(
                (key) => key !== 'notes'
              );
              const missing = requiredKeys.filter(
                (key) => !canonicalToActualHeader[key]
              );
              if (missing.length) {
                throw new MissingRequiredCsvColumnsError(
                  `Missing required headers: ${missing.join(', ')}`,
                  missing.map((key) => HEADER_ALIASES[key][0])
                );
              }

              // 4. Normalize rows to canonical keys
              const normalizedData: csvItem[] = results.data.map((row) => {
                const normalized: csvItem = {} as csvItem;

                for (const canonicalKey of canonicalKeys) {
                  const headerName = canonicalToActualHeader[canonicalKey]; // JP or EN
                  if (headerName) {
                    const value = row[headerName];
                    normalized[canonicalKey as keyof csvItem] =
                      typeof value === 'string' ? value.normalize('NFC') : '';
                  }
                }

                return normalized;
              });

              resolve(normalizedData);
            } catch (error) {
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          },
          error: (error: Error) => {
            reject(error);
          },
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const updateChangeDateType = (dateString: string | null | undefined) => {
  if (dateString) {
    const date = new Date(dateString);
    const changedDateString =
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0');

    return changedDateString;
  } else return null;
};

export const updateCSVFileDateFormatType = (csvDatas: csvItem[]) => {
  return csvDatas.map((data) => {
    let {
      riskAssessDate: risk_assess_date,
      supplierConfirmationDate: supplier_confirmation_date,
    } = data;

    // Format リスクアセスメント実施日 if it exists and is not already in YYYY-MM-DD format
    if (risk_assess_date && !/^\d{4}-\d{2}-\d{2}$/.test(risk_assess_date)) {
      const date = new Date(risk_assess_date);
      risk_assess_date =
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0');
    }

    // Format サプライヤーへの確認日 if it exists and is not already in YYYY-MM-DD format
    if (
      supplier_confirmation_date &&
      !/^\d{4}-\d{2}-\d{2}$/.test(supplier_confirmation_date)
    ) {
      const date = new Date(supplier_confirmation_date);
      supplier_confirmation_date =
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0');
    }

    // Return the updated data object
    return {
      ...data,
      riskAssessDate: risk_assess_date,
      supplierConfirmationDate: supplier_confirmation_date,
    };
  });
};
